import React from "react";
import _ from "lodash";

import {Grid, makeStyles} from "@material-ui/core";
import {ArrowBack} from "@material-ui/icons";

import BaseDialog from "../../../../../../../../components/BaseDialog/BaseDialog";
import PrimaryButton from "../../../../../../../../components/Buttons/PrimaryButton";
import {ModelportfoliosFilters} from "../../../../../../../Modelportfolios";
import FavoriteListSelection
  from "../../../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/components/FavoriteListSelection/FavoriteListSelection";
import MusterDepot
  from "../../../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/components/MusterDepot/MusterDepot";
import useCancelSelection
  from "../../../../../../../RiskProfiling/components/StepContent/components/step/ProductSelectionStepsV2/components/AssetsSelection/hooks/useCancelSelection";
import Portfolio from "../../../../../TradeStep/components/Portfolio";

import {STEPS, SOURCES} from "./constants";
import MainStep from "./MainStep";

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    paddingLeft: 0,
    paddingRight: 0,
  },
  header: {
    fontSize: 15,
    fontFamily: "Roboto-Bold",
  },
});


/**
 * Shared dialog shell for instrument selection modals.
 * Renders the step-based wizard (Filters / Main / Source).
 * Consumers provide `hook` (from useInstrumentSelection) and optional `children` for MainStep.
 */
const InstrumentSelectionDialog = ({
  hook,
  instrumentsHandler,
  portfolioInstruments,
  predefinedFilter,
  customerId,
  isCustomerApp,
  isVirtual,
  onClose,
  dataId,
  disabledIsins,
  children,
}) => {
  const classes = useStyles();
  const {step, activeSource, activeSourceItem, activeAssets} = hook;

  const currentDepotText = isVirtual
    ? (isCustomerApp ? "Virtuellem Depot" : "Musterdepot")
    : 'Kundendepot';
  const currentDepotSelectionText = `Aus ${currentDepotText} wählen`;

  const instrumentsSources = React.useMemo(() => {
    const sources = [];
    if (!isCustomerApp) {
      sources.push({
        source: SOURCES.FAVOURITE_LIST,
        text: "Aus Favoritenlisten wählen",
        variant: "outlined",
        onButtonClick: () => hook.selectSource(SOURCES.FAVOURITE_LIST),
      });
      sources.push({
        source: SOURCES.MUSTER_DEPOT,
        text: isVirtual ? "Anderes Musterdepot auswählen" : "Musterdepot auswählen",
        variant: "outlined",
        onButtonClick: () => hook.selectSource(SOURCES.MUSTER_DEPOT),
      });
    }
    if (portfolioInstruments && portfolioInstruments.length) {
      sources.push({
        source: SOURCES.PORTFOLIO,
        text: currentDepotSelectionText,
        variant: "outlined",
        onButtonClick: () => hook.selectSource(SOURCES.PORTFOLIO),
      });
    }
    return sources;
  }, [isCustomerApp, isVirtual, portfolioInstruments, hook.selectSource]);

  const checkItemsInExistingPortfolio = React.useCallback((items, skipDetails) => {
    const existingISINs = new Set((portfolioInstruments || []).map(i => i.isin));

    return items.reduce((acc, item) => {
      const exists = existingISINs.has(item.isin);
      if (!skipDetails) {
        if (exists) {
          item.disabledByFilter = true;
          item.disabledByAcceptanceCriterias = {
            general: {
              in_portfolio: {
                title: `Investment ist bereits im ${currentDepotText} vorhanden. Bitte Investment über "${currentDepotSelectionText}" hinzufügen`,
                value: false,
              }
            }
          };
        }
        acc.push(item);
      } else if (!exists) {
        acc.push(item);
      }
      return acc;
    }, []);
  }, [portfolioInstruments, currentDepotText]);

  const selectedAssets = React.useMemo(() => _.filter(hook.selectedAssets, 'selected'), [hook.selectedAssets]);

  const cancelSelection = useCancelSelection({
    hasSelectedAssets: !_.isEmpty(selectedAssets),
    handleClose: onClose,
    handleNextClick: hook.handleSubmit
  });

  const activeSourceTitle = React.useMemo(
    () => (instrumentsSources.find(s => s.source === activeSource) || {}).text,
    [instrumentsSources, activeSource]
  );

  const currentConfig = {
    [STEPS.FILTERS]: {
      title: (
        <>
          <PrimaryButton
            text="Schließen"
            icon={<i className="fa fa-chevron-left" />}
            variant={'text'}
            onButtonClick={hook.goBack}
          /> Investmentsuche
        </>
      ),
    },
    [STEPS.SOURCE_LIST]: {
      title: activeSourceTitle,
      actions: {backButton: true},
      customClasses: {dialogContent: classes.dialogContent},
    },
    [STEPS.SOURCE_INSTRUMENTS]: {
      title: activeSourceTitle,
      actions: {backButton: true, onNext: hook.handleSourceNext},
      customClasses: {dialogContent: classes.dialogContent},
    },
    [STEPS.PORTFOLIO]: {
      title: activeSourceTitle,
      actions: {backButton: true, onNext: hook.handleSourceNext},
    },
    [STEPS.MAIN]: {
      title: 'Investment hinzufügen',
      actions: {onNext: hook.handleSubmit},
    },
  }[step];

  const isMainStep = step === STEPS.MAIN;
  const isSourceStep = !isMainStep && [STEPS.SOURCE_LIST, STEPS.SOURCE_INSTRUMENTS].includes(step);

  const actions = currentConfig.actions ? (
    <Grid container spacing={2} justify={"flex-end"}>
      {isMainStep && (
        <Grid item>
          <PrimaryButton
            variant="outlined"
            text="Abbrechen"
            onButtonClick={cancelSelection}
          />
        </Grid>
      )}
      {currentConfig.actions.backButton && (
        <Grid item>
          <PrimaryButton
            variant="outlined"
            text="Zurück"
            onButtonClick={hook.goBack}
          />
        </Grid>
      )}
      {currentConfig.actions.onNext && (
        <Grid item>
          <PrimaryButton
            disabled={hook.nextDisabled}
            text="Speichern & fortfahren"
            onButtonClick={currentConfig.actions.onNext}
          />
        </Grid>
      )}
    </Grid>
  ) : undefined;

  return (
    <BaseDialog
      open={true}
      maxWidth="lg"
      fullWidth
      disableBackdropClick
      handleClose={isMainStep ? cancelSelection : hook.goBack}
      title={currentConfig.title}
      actions={actions}
      customClasses={currentConfig.customClasses}
      data-id={dataId}
    >
      {step === STEPS.FILTERS && (
        <ModelportfoliosFilters
          onAddInstruments={hook.onFiltersAddInstruments}
          instrumentsHandler={instrumentsHandler}
          addExtraFilters={hook.addExtraFilters}
          useButtons
          hideTitle
          predefinedFilter={predefinedFilter}
          customerId={customerId}
        />
      )}

      {isMainStep && (
        <MainStep
          classes={classes}
          assetsSearch={hook.assetsSearch}
          handleSearchActivate={hook.handleSearchActivate}
          disabledInstruments={hook.disabledInstruments}
          handleAddAssetFromSearch={hook.handleAddAssetFromSearch}
          handleToggleAsset={hook.handleToggleAsset}
          onFiltersClick={hook.onFiltersClick}
          instrumentsSources={instrumentsSources}
          isCustomerApp={isCustomerApp}
          assets={hook.selectedAssets}
          selectedAssets={selectedAssets}
          instrumentsHandler={instrumentsHandler}
          onSelectAll={hook.onSelectAllAssets}
        >
          {children}
        </MainStep>
      )}

      {isSourceStep && activeSource === SOURCES.FAVOURITE_LIST && (
        <FavoriteListSelection
          onDetailsClick={hook.handleDetailsClick}
          instrumentsHandler={instrumentsHandler}
          onInstrumentSelect={hook.onInstrumentSelect}
          onSelectAll={hook.onSelectAll}
          selectedItem={activeSourceItem}
          selectedInstruments={activeAssets}
          instrumentsToFilterOut={disabledIsins}
          checkItemsInExistingPortfolio={checkItemsInExistingPortfolio}
          hideTitle
        />
      )}

      {isSourceStep && activeSource === SOURCES.MUSTER_DEPOT && (
        <MusterDepot
          onDetailsClick={hook.handleDetailsClick}
          instrumentsHandler={instrumentsHandler}
          onInstrumentSelect={hook.onInstrumentSelect}
          onSelectAll={hook.onSelectAll}
          selectedItem={activeSourceItem}
          selectedInstruments={activeAssets}
          checkItemsInExistingPortfolio={checkItemsInExistingPortfolio}
          hideTitle
        />
      )}

      {step === STEPS.PORTFOLIO && (
        <Portfolio
          portfolioInstruments={portfolioInstruments}
          instrumentsHandler={instrumentsHandler}
          selectedAssets={activeAssets}
          onInstrumentSelect={hook.onInstrumentSelect}
          onSelectAll={hook.onSelectAll}
        />
      )}
    </BaseDialog>
  );
};

export default InstrumentSelectionDialog;
